import type { ProjectPrebuildConfiguration } from "gitpod-next-api/gitpod/v1/project_pb";
import type { PlainMessage } from "@bufbuild/protobuf";

/**
 * Formats the hour in 12-hour format with AM/PM
 */
const formatHour12 = (hourUtc: number): string => {
    const hour12 = hourUtc % 12 || 12;
    const ampm = hourUtc < 12 ? "AM" : "PM";
    return `${hour12}:00 ${ampm}`;
};

/**
 * Gets a short schedule label for the project card
 * @param prebuildConfig - The project's prebuild configuration
 * @returns A short label like "Prebuilds daily at 6:00 AM" or undefined if not enabled
 */
export const getPrebuildScheduleLabel = (
    prebuildConfig: PlainMessage<ProjectPrebuildConfiguration> | undefined,
): string | undefined => {
    if (!prebuildConfig?.enabled) {
        return undefined;
    }

    if (prebuildConfig.trigger?.trigger?.case === "dailySchedule") {
        const hourUtc = prebuildConfig.trigger.trigger.value.hourUtc;
        return `Prebuilds daily at ${formatHour12(hourUtc)} UTC`;
    }

    return undefined;
};

/**
 * Calculates the schedule message for the prebuild empty state
 * @param prebuildConfig - The project's prebuild configuration
 * @returns A message describing when the next prebuild will run or that prebuilds are disabled
 */
export const getPrebuildScheduleMessage = (
    prebuildConfig: PlainMessage<ProjectPrebuildConfiguration> | undefined,
): string => {
    const prebuildEnabled = prebuildConfig?.enabled || false;
    const scheduledHourUtc =
        prebuildConfig?.trigger?.trigger?.case === "dailySchedule"
            ? prebuildConfig.trigger.trigger.value.hourUtc
            : undefined;

    if (!prebuildEnabled) {
        return "Prebuilds are currently disabled. You can enable them in the project settings.";
    }

    if (scheduledHourUtc === undefined) {
        return "Prebuilds are currently disabled. You can enable them in the project settings.";
    }

    const now = new Date();
    const nextRun = new Date();
    nextRun.setUTCHours(scheduledHourUtc, 0, 0, 0);

    // If the scheduled time has passed today, schedule for tomorrow
    if (nextRun <= now) {
        nextRun.setUTCDate(nextRun.getUTCDate() + 1);
    }

    const isToday =
        nextRun.getUTCDate() === now.getUTCDate() &&
        nextRun.getUTCMonth() === now.getUTCMonth() &&
        nextRun.getUTCFullYear() === now.getUTCFullYear();

    const timeStr = `${scheduledHourUtc.toString().padStart(2, "0")}:00`;
    return `The first run is scheduled for ${isToday ? "today" : "tomorrow"} at ${timeStr} UTC.`;
};
